import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';

import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import useClassicComparatorModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorModel';

type ModelField = 'model' | 'date' | 'runtime' | 'member' | 'rmv' | '';

export interface ModelSectionProps {
  model: string;
  setModel: (value: string) => void;
  dateDiff: string;
  setDateDiff: (value: string | undefined) => void;
  runtime: number | undefined;
  setRuntime: (value: number | undefined) => void;
  member: string | undefined;
  setMember: (value: string | undefined) => void;
  rmv: boolean;
  setRMV: (value: boolean) => void;
  isBaseModel: boolean;
}

/**
 * Sub-accordion de um modelo (base ou confrontante) dentro do painel mobile.
 * A normalização e as opções derivadas vêm do `useClassicComparatorModel`, o mesmo
 * hook que o `ModelAccordion` do desktop usa.
 */
export default function ModelSection({
  model,
  setModel,
  dateDiff,
  setDateDiff,
  runtime,
  setRuntime,
  member,
  setMember,
  rmv,
  setRMV,
  isBaseModel,
}: ModelSectionProps) {
  const [openField, setOpenField] = useState<ModelField>('');
  const toggleField = (field: ModelField) => () => (
    setOpenField((current) => (current === field ? '' : field))
  );

  const {
    isFetching,
    modelOptions,
    availableIntervals,
    runtimeOptions,
    memberOptions,
    date,
    setDate,
    selectModel,
  } = useClassicComparatorModel({
    model,
    setModel,
    dateDiff,
    setDateDiff,
    runtime,
    setRuntime,
    member,
    setMember,
    isBaseModel,
  });

  if (isFetching) {
    return (
      <div className="cc-mobile-sheet-loading">
        <Spinner />
      </div>
    );
  }

  // Modelos são exibidos em caixa alta no desktop (`text-uppercase` no ModelAccordion).
  const modelLabel = modelOptions.find((option) => option.value === model)?.label.toUpperCase();
  const runtimeLabel = runtimeOptions.find((option) => option.id === runtime)?.label;
  const memberLabel = memberOptions.find((option) => option.id === member)?.label;
  const dateLabel = date ? date.toLocaleDateString('pt-BR') : undefined;

  return (
    <div className="cc-mobile-model-section">
      <Section
        label="Modelo"
        valueText={modelLabel}
        open={openField === 'model'}
        onToggle={toggleField('model')}
      >
        {modelOptions.length === 0 && (
          <p className="cc-mobile-sheet-hint">Selecione a variável primeiro</p>
        )}
        {modelOptions.map((option) => (
          <OptionRow
            key={option.value}
            label={option.label.toUpperCase()}
            selected={model === option.value}
            onSelect={() => {
              selectModel(option.value);
              setOpenField('');
            }}
          />
        ))}
      </Section>

      <Section
        label="Data de previsão"
        valueText={dateLabel}
        open={openField === 'date'}
        onToggle={toggleField('date')}
      >
        {availableIntervals.length === 0 && (
          <p className="cc-mobile-sheet-hint">Selecione o modelo primeiro</p>
        )}
        {availableIntervals.length > 0 && (
          <div className="cc-mobile-sheet-datepicker">
            <DatePicker
              inline
              selected={date}
              onChange={(value) => {
                setDate(value);
                setOpenField('');
              }}
              includeDateIntervals={availableIntervals}
            />
          </div>
        )}
      </Section>

      <Section
        label="Runtime"
        valueText={runtimeLabel}
        open={openField === 'runtime'}
        onToggle={toggleField('runtime')}
      >
        {runtimeOptions.map((option) => (
          <OptionRow
            key={option.id}
            label={option.label}
            selected={runtime === option.id}
            onSelect={() => {
              setRuntime(option.id);
              setOpenField('');
            }}
          />
        ))}
      </Section>

      <Section
        label="Membro"
        valueText={memberLabel}
        open={openField === 'member'}
        onToggle={toggleField('member')}
      >
        {memberOptions.map((option) => (
          <OptionRow
            key={option.id}
            label={option.label}
            selected={member === option.id}
            onSelect={() => {
              setMember(option.id);
              setOpenField('');
            }}
          />
        ))}
      </Section>

      <Section
        label="RMV"
        valueText={rmv ? 'Com RMV' : 'Sem RMV'}
        open={openField === 'rmv'}
        onToggle={toggleField('rmv')}
      >
        <OptionRow
          label="Com RMV"
          selected={rmv}
          onSelect={() => {
            setRMV(true);
            setOpenField('');
          }}
        />
        <OptionRow
          label="Sem RMV"
          selected={!rmv}
          onSelect={() => {
            setRMV(false);
            setOpenField('');
          }}
        />
      </Section>
    </div>
  );
}
